import React, { useEffect, useState, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, MoreHorizontal, Eye } from 'lucide-react';
import { toast } from 'sonner';
import AdminTable from '@/components/AdminTable';

const STATUS_BADGE = {
  pending: { label: 'Pending', style: { backgroundColor: '#fef3c7', color: '#d97706' } },
  approved: { label: 'Approved', style: { backgroundColor: '#dcfce7', color: '#16a34a' } },
  rejected: { label: 'Rejected', style: { backgroundColor: '#fee2e2', color: '#dc2626' } },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewDialog, setReviewDialog] = useState(null); // { user, action }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  const fetchApplications = useCallback((status) => {
    setLoading(true);
    const params = status === 'all' ? '' : `?status=${status}`;
    api.get(`/admin/applications${params}`)
      .then(r => setApplications(r.data))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchApplications(filter); }, [filter, fetchApplications]);

  const openReview = (user, action) => {
    setReviewDialog({ user, action });
    setNotes('');
  };

  const submitReview = async () => {
    if (!reviewDialog) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/applications/${reviewDialog.user.id}/review`, {
        action: reviewDialog.action,
        admin_notes: notes,
      });
      toast.success(`Application ${reviewDialog.action} successfully`);
      setReviewDialog(null);
      fetchApplications(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell title="Applications">
      <div className="space-y-6">
        {/* Filter tabs */}
        <div className="flex items-center justify-between">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-gray-100 rounded-lg p-1">
              <TabsTrigger value="pending" className="rounded-md">Pending</TabsTrigger>
              <TabsTrigger value="approved" className="rounded-md">Approved</TabsTrigger>
              <TabsTrigger value="rejected" className="rounded-md">Rejected</TabsTrigger>
              <TabsTrigger value="all" className="rounded-md">All</TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="text-xs text-gray-500 font-medium">{applications.length} result{applications.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        <AdminTable
          data={applications}
          loading={loading}
          dataTestId="admin-applications-table"
          emptyTitle={`No ${filter === 'all' ? '' : filter} applications`}
          emptySubtitle="Applications will appear here once submitted."
          columns={[
            {
              key: 'applicant', label: 'Applicant',
              render: (app) => (
                <div>
                  <p className="font-semibold text-gray-900">{app.name}</p>
                  <p className="text-xs text-gray-500">{app.email}</p>
                </div>
              ),
            },
            { key: 'applied', label: 'Applied', render: (app) => <span className="text-gray-500 font-medium">{formatDate(app.membership_applied_at)}</span> },
            { key: 'role', label: 'Role', render: (app) => <span className="text-xs capitalize font-medium text-gray-500">{app.seller_type || '—'}</span> },
            {
              key: 'status', label: 'Status',
              render: (app) => {
                const badge = STATUS_BADGE[app.membership_status] || STATUS_BADGE.pending;
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold" style={badge.style}>{badge.label}</span>;
              },
            },
            {
              key: 'actions', label: '', width: '60px',
              render: (app) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-gray-100 rounded-xl">
                      <MoreHorizontal size={15} className="text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border border-gray-100 shadow-lg rounded-xl">
                    <DropdownMenuItem onClick={() => setDetailUser(app)} className="gap-2 cursor-pointer hover:bg-gray-50 rounded-lg">
                      <Eye size={14} className="text-gray-500" /> <span className="text-gray-700 font-medium">View details</span>
                    </DropdownMenuItem>
                    {app.membership_status === 'pending' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => openReview(app, 'approved')}
                          className="gap-2 cursor-pointer hover:bg-green-50 rounded-lg text-green-600 font-medium"
                          data-testid="admin-approve-button"
                        >
                          <CheckCircle size={14} /> Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openReview(app, 'rejected')}
                          className="gap-2 cursor-pointer hover:bg-red-50 rounded-lg text-red-600 font-medium"
                          data-testid="admin-reject-button"
                        >
                          <XCircle size={14} /> Reject
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </div>

      {/* Review Confirmation Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="bg-white border-0 shadow-xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {reviewDialog?.action === 'approved' ? 'Approve' : 'Reject'} application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500 leading-relaxed">
              {reviewDialog?.action === 'approved'
                ? `${reviewDialog?.user?.name} will be notified and granted member access.`
                : `${reviewDialog?.user?.name} will be notified that their application was not successful.`
              }
            </p>
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                Admin notes {reviewDialog?.action === 'rejected' ? '(required)' : '(optional)'}
              </label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add a note for the applicant…"
                rows={3}
                className="text-sm border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setReviewDialog(null)} disabled={submitting} className="rounded-xl hover:bg-gray-100 font-semibold text-gray-600">
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={submitting || (reviewDialog?.action === 'rejected' && !notes.trim())}
              className={`rounded-xl font-semibold shadow-sm text-white ${reviewDialog?.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {submitting ? 'Processing…' : reviewDialog?.action === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailUser} onOpenChange={() => setDetailUser(null)}>
        <DialogContent className="bg-white border-0 shadow-xl rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Applicant details</DialogTitle>
          </DialogHeader>
          {detailUser && (
            <div className="space-y-4 py-2 text-sm">
              {[
                ['Name', detailUser.name],
                ['Email', detailUser.email],
                ['Seller type', detailUser.seller_type || '—'],
                ['Location', detailUser.location || '—'],
                ['Bio', detailUser.bio || '—'],
                ['Applied', formatDate(detailUser.membership_applied_at)],
                ['Status', detailUser.membership_status],
                ['Admin notes', detailUser.membership_admin_notes || '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="w-28 flex-shrink-0 font-bold text-xs uppercase tracking-wider text-gray-500 self-center">{k}</span>
                  <span className="text-gray-900 font-medium leading-relaxed">{v}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDetailUser(null)} className="rounded-xl font-semibold text-gray-600 hover:bg-gray-100">Close</Button>
            {detailUser?.membership_status === 'pending' && (
              <>
                <Button
                  onClick={() => { setDetailUser(null); openReview(detailUser, 'approved'); }}
                  className="rounded-xl bg-green-600 hover:bg-green-700 font-semibold text-white shadow-sm"
                  data-testid="admin-approve-button"
                >
                  Approve
                </Button>
                <Button
                  onClick={() => { setDetailUser(null); openReview(detailUser, 'rejected'); }}
                  className="rounded-xl bg-red-600 hover:bg-red-700 font-semibold text-white shadow-sm"
                  data-testid="admin-reject-button"
                >
                  Reject
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
